import { flattenBlocks } from './parser.js';
/**
 * Pixel scroll position at `nowMs`, given a starting `basePx` that was true at
 * `anchorTimeMs`, a `speedPxPerSec` and whether scrolling is `running`.
 *
 * Frame rate is irrelevant: only the time delta matters. When paused, the position
 * is simply the base.
 */
export function positionAt(basePx, anchorTimeMs, speedPxPerSec, running, nowMs) {
    if (!running)
        return basePx;
    const elapsedSec = Math.max(0, nowMs - anchorTimeMs) / 1000;
    return basePx + speedPxPerSec * elapsedSec;
}
/**
 * Clamp a scroll position to the scrollable range [0, maxScrollPx].
 * `maxScrollPx` = contentHeight - viewportHeight (never negative).
 */
export function clampPosition(px, maxScrollPx) {
    if (px < 0)
        return 0;
    if (px > maxScrollPx)
        return Math.max(0, maxScrollPx);
    return px;
}
/** Steps for the speed +/- controls, in px/sec. Centralised so UI + tests agree. */
export const SPEED_STEP_PX_PER_SEC = 15;
export const MIN_SPEED_PX_PER_SEC = 0;
export const MAX_SPEED_PX_PER_SEC = 600;
export function clampSpeed(value) {
    return Math.min(MAX_SPEED_PX_PER_SEC, Math.max(MIN_SPEED_PX_PER_SEC, value));
}
export function stepSpeed(current, direction) {
    return clampSpeed(current + direction * SPEED_STEP_PX_PER_SEC);
}
/** Manual jog distances in BLOCK units (see applyScrollControl 'nudge'). */
export const NUDGE_SMALL = 0.5;
export const NUDGE_LARGE = 3;
/** Flattened index of the first block of each non-empty section, in reading order. */
export function sectionStartIndexes(project) {
    const starts = [];
    let count = 0;
    for (const section of project.sections) {
        if (section.blocks.length > 0)
            starts.push(count);
        count += section.blocks.length;
    }
    return starts;
}
/**
 * Apply a producer scroll control to the authoritative {@link ScrollState}.
 *
 * Pure and DOM-free: the position is expressed in BLOCK units (block index +
 * offsetFraction), which every client can map to pixels using its own layout. The
 * presenter reports concrete pixel positions back via `scroll:reanchor` so late
 * joiners and reconnects stay accurate.
 */
export function applyScrollControl(state, control, project, nowMs) {
    const blocks = flattenBlocks(project);
    if (blocks.length === 0)
        return { ...state, running: false };
    const lastIdx = blocks.length - 1;
    const clampIdx = (i) => Math.min(lastIdx, Math.max(0, i));
    const foundIdx = blocks.findIndex((b) => b.id === state.anchor.blockId);
    const idx = foundIdx < 0 ? 0 : foundIdx;
    const anchorAt = (blockIndex, offsetFraction) => ({
        blockId: blocks[clampIdx(blockIndex)].id,
        offsetFraction: Math.min(1, Math.max(0, offsetFraction)),
        atTime: nowMs,
    });
    const restamp = () => ({ ...state.anchor, atTime: nowMs });
    switch (control.action) {
        case 'play':
            return { ...state, running: true, anchor: restamp() };
        case 'pause':
            return { ...state, running: false, anchor: restamp() };
        case 'toggle':
            return { ...state, running: !state.running, anchor: restamp() };
        case 'setSpeed':
            return { ...state, speedPxPerSec: clampSpeed(control.value ?? state.speedPxPerSec), anchor: restamp() };
        case 'speedUp':
            return { ...state, speedPxPerSec: stepSpeed(state.speedPxPerSec, 1), anchor: restamp() };
        case 'speedDown':
            return { ...state, speedPxPerSec: stepSpeed(state.speedPxPerSec, -1), anchor: restamp() };
        case 'nudge': {
            const delta = control.value ?? NUDGE_SMALL;
            const p = Math.min(lastIdx, Math.max(0, idx + state.anchor.offsetFraction + delta));
            const newIdx = Math.floor(p);
            return { ...state, anchor: anchorAt(newIdx, p - newIdx) };
        }
        case 'top':
            return { ...state, anchor: anchorAt(0, 0) };
        case 'sectionPrev':
        case 'sectionNext': {
            const starts = sectionStartIndexes(project);
            if (starts.length === 0)
                return { ...state, anchor: anchorAt(0, 0) };
            // Index of the section that currently contains the anchor.
            let sec = 0;
            for (let i = 0; i < starts.length; i += 1)
                if (starts[i] <= idx)
                    sec = i;
            const targetSec = control.action === 'sectionNext'
                ? Math.min(starts.length - 1, sec + 1)
                : Math.max(0, sec - 1);
            return { ...state, anchor: anchorAt(starts[targetSec], 0) };
        }
        case 'goto': {
            // control.value (0..1) optionally centres a precise point within the block
            // (used by voice-follow to place the spoken word on the reading band).
            const target = control.blockId ? blocks.findIndex((b) => b.id === control.blockId) : idx;
            return { ...state, anchor: anchorAt(target < 0 ? idx : target, control.value ?? 0) };
        }
        default:
            return state;
    }
}
/**
 * Resolve the concrete pixel position an authoritative {@link ScrollState} implies
 * right now, given how the client maps its anchor block to a pixel offset. The
 * caller supplies `blockTopPx` (pixel top of the anchor block) and `blockHeightPx`
 * so this stays DOM-free.
 */
export function resolveScrollPx(state, blockTopPx, blockHeightPx, nowMs) {
    const basePx = blockTopPx + state.anchor.offsetFraction * blockHeightPx;
    return positionAt(basePx, state.anchor.atTime, state.speedPxPerSec, state.running, nowMs);
}
//# sourceMappingURL=scroll.js.map